import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Video,
  FileText,
  TrendingUp,
  UserCheck,
  Filter,
  Briefcase,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface Stats {
  totalUsers: number
  totalSessions: number
  activeSessions: number
  totalEvaluations: number
}

interface Session {
  id: number
  client_name: string
  interviewer_name?: string
  interviewer_alias?: string
  title: string
  scheduled_at: string
  status: string
}

export default function AdminHome() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalSessions: 0,
    activeSessions: 0,
    totalEvaluations: 0,
  })
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState('30d')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, sessionsData] = await Promise.all([
          adminApi.getStats(),
          adminApi.getSessions(),
        ])
        if (statsData) setStats(statsData)
        const allSessions = sessionsData.data || sessionsData || []
        setSessions(allSessions.slice(0, 8))
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const completionRate =
    sessions.length > 0
      ? Math.round(
          (sessions.filter((s) => s.status === 'completed').length /
            sessions.length) *
            100
        )
      : 0

  if (loading) {
    return <div className="loading-screen">Loading dashboard...</div>
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">
            Welcome back! Here is an overview of your platform.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} style={{ color: 'var(--text-muted)' }} />
          <select
            className="form-input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ width: 'auto', padding: '8px 12px' }}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon primary">
            <Users size={24} />
          </div>
          <div className="value">{stats.totalUsers}</div>
          <div className="label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <UserCheck size={24} />
          </div>
          <div className="value">{stats.activeSessions}</div>
          <div className="label">Active Sessions</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <Video size={24} />
          </div>
          <div className="value">{stats.totalSessions}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <FileText size={24} />
          </div>
          <div className="value">{stats.totalEvaluations}</div>
          <div className="label">Evaluations</div>
        </div>
      </div>

      {/* Metrics Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
          marginBottom: 32,
        }}
      >
        <div
          className="card"
          style={{ padding: 20 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Completion Rate
            </span>
            <span
              className="badge badge-success"
              style={{ fontWeight: 600 }}
            >
              {completionRate}%
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${completionRate}%`,
                background: 'var(--success)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Avg. Session Rating
            </span>
            <span
              className="badge badge-primary"
              style={{ fontWeight: 600 }}
            >
              4.2 / 5
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '84%',
                background: 'var(--primary-navy)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Client Satisfaction
            </span>
            <span
              className="badge badge-success"
              style={{ fontWeight: 600 }}
            >
              92%
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '92%',
                background: 'var(--success)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 24,
        }}
      >
        {/* Recent Sessions */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Recent Sessions
            </h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
              onClick={() => navigate('/admin/sessions')}
            >
              View All
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Interviewer</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: 'var(--text-muted)',
                      }}
                    >
                      No sessions yet
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div className="avatar">
                            {(s.client_name || 'C').charAt(0)}
                          </div>
                          <span style={{ fontWeight: 500 }}>
                            {s.client_name}
                          </span>
                        </div>
                      </td>
                      <td>
                        {s.interviewer_alias ||
                          s.interviewer_name ||
                          'N/A'}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {new Date(s.scheduled_at).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                          }
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${s.status === 'completed' ? 'badge-success' : s.status === 'scheduled' ? 'badge-primary' : s.status === 'cancelled' ? 'badge-danger' : 'badge-warning'}`}
                        >
                          {s.status.charAt(0).toUpperCase() +
                            s.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            Quick Actions
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/content')}
            >
              <FileText size={18} />
              Review Evaluations
            </button>
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/applications')}
            >
              <Briefcase size={18} />
              Interviewer Applications
            </button>
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/reports')}
            >
              <TrendingUp size={18} />
              Generate Report
            </button>
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/users')}
            >
              <Users size={18} />
              Manage Users
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
